import { log } from '../logger';

/**
 * Microphone audio capture via AudioWorklet.
 *
 * Captures mono PCM16 @ 24 kHz and delivers chunks to a callback
 * for WebSocket transmission to the voice relay.
 *
 * On platforms where AudioContext ignores the requested sampleRate
 * (e.g. iOS Safari always uses the hardware rate), the PCM worklet
 * automatically downsamples to 24 kHz via linear interpolation.
 */

const TARGET_SAMPLE_RATE = 24_000;

export class AudioCapture {
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private analyserNode: AnalyserNode | null = null;
  private onChunk: ((pcm16: ArrayBuffer) => void) | null = null;

  /**
   * Start capturing audio from the microphone.
   * @param onChunk Called with PCM16 ArrayBuffer chunks (~128 samples each).
   */
  async start(onChunk: (pcm16: ArrayBuffer) => void): Promise<void> {
    this.onChunk = onChunk;

    // Request microphone access.
    // Use `ideal` for sampleRate so browsers that don't support 24 kHz
    // (e.g. iOS Safari) won't throw OverconstrainedError.
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        sampleRate: { ideal: TARGET_SAMPLE_RATE },
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });

    // Create context at target sample rate (hint — iOS Safari ignores this
    // and always uses the hardware rate, typically 48 kHz).
    this.ctx = new AudioContext({ sampleRate: TARGET_SAMPLE_RATE });

    // Mobile browsers start AudioContext suspended; must resume during
    // a user gesture (startSession is triggered by button tap).
    if (this.ctx.state === "suspended") {
      await this.ctx.resume();
    }

    const actualRate = this.ctx.sampleRate;
    const needsResample = Math.abs(actualRate - TARGET_SAMPLE_RATE) > 1;
    log.debug('VOICE', `sampleRate: actual=${actualRate} target=${TARGET_SAMPLE_RATE} resample=${needsResample}`);

    // Load the PCM capture worklet
    await this.ctx.audioWorklet.addModule("/pcm-worklet.js");

    // Build the audio graph: mic → analyser → worklet
    this.sourceNode = this.ctx.createMediaStreamSource(this.stream);
    this.analyserNode = this.ctx.createAnalyser();
    this.analyserNode.fftSize = 256;

    this.workletNode = new AudioWorkletNode(this.ctx, "pcm-capture-processor");

    // If the context runs at a different rate than 24 kHz (e.g. iOS at 48 kHz),
    // tell the worklet to downsample via linear interpolation.
    if (needsResample) {
      this.workletNode.port.postMessage({
        type: "set-sample-rate",
        sourceSampleRate: actualRate,
        targetSampleRate: TARGET_SAMPLE_RATE,
      });
    }

    let workletMsgCount = 0;
    this.workletNode.port.onmessage = (e: MessageEvent<ArrayBuffer>) => {
      workletMsgCount++;
      if (workletMsgCount <= 3 || workletMsgCount % 200 === 0) {
        log.debug('VOICE', `worklet msg #${workletMsgCount}, ${e.data.byteLength}B, hasCallback=${!!this.onChunk}`);
      }
      this.onChunk?.(e.data);
    };

    this.sourceNode.connect(this.analyserNode);
    this.analyserNode.connect(this.workletNode);
    // Do NOT connect workletNode to destination — we don't want local playback of mic.
  }

  /** Get the AnalyserNode for waveform visualisation. */
  getAnalyser(): AnalyserNode | null {
    return this.analyserNode;
  }

  /** Stop capturing and release all resources. */
  async stop(): Promise<void> {
    this.onChunk = null;

    this.workletNode?.disconnect();
    this.sourceNode?.disconnect();
    this.analyserNode?.disconnect();
    this.workletNode = null;
    this.sourceNode = null;
    this.analyserNode = null;

    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;

    if (this.ctx && this.ctx.state !== "closed") {
      await this.ctx.close();
    }
    this.ctx = null;
  }
}
